import React from 'react';
import { motion } from 'framer-motion';
import { useAnimatedCounter } from '../hooks/useAnimatedCounter';
import { Users, Lightbulb, TrendingUp, ShieldAlert } from 'lucide-react';
function StatCounter({
  target,
  label,
  suffix = ''
}: {
  target: number;
  label: string;
  suffix?: string;
}) {
  const {
    count,
    ref
  } = useAnimatedCounter(target);
  return <div ref={ref} className="flex flex-col items-center text-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100" data-id="element-12">
      <div className="text-4xl md:text-5xl font-serif font-bold text-brand-teal mb-2" data-id="element-13">
        {count}
        {suffix}
      </div>
      <div className="text-sm md:text-base text-gray-600 font-medium uppercase tracking-wide" data-id="element-14">
        {label}
      </div>
    </div>;
}
export function AboutSection() {
  const findings = [{
    icon: <Lightbulb className="w-6 h-6 text-brand-teal" data-id="element-15" />,
    title: 'Hidden Pathways',
    desc: 'Uncovering non-traditional routes into business ownership for diverse leaders.'
  }, {
    icon: <ShieldAlert className="w-6 h-6 text-brand-teal" data-id="element-16" />,
    title: 'Systemic Barriers',
    desc: 'Identifying the specific hurdles diverse entrepreneurs face in acquisition.'
  }, {
    icon: <TrendingUp className="w-6 h-6 text-brand-teal" data-id="element-17" />,
    title: 'Lessons Learned',
    desc: 'Actionable insights from those who have successfully bought and sold.'
  }, {
    icon: <Users className="w-6 h-6 text-brand-teal" data-id="element-18" />,
    title: 'Redesigning Succession',
    desc: 'Opportunities to build a more inclusive ecosystem for the next generation.'
  }];
  return <section className="py-24 bg-brand-white relative" data-id="element-19">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-20">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-16" data-id="element-21">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-black mb-6" data-id="element-22">
            What We Discovered
          </h2>
          <p className="text-xl text-gray-600 font-light leading-relaxed" data-id="element-23">
            We interviewed diverse and inspiring entrepreneurs who have both
            sold and acquired businesses across Canada, alongside executive
            insights on search funds and acquisition entrepreneurship.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20" data-id="element-24">
          <StatCounter target={30} label="Hours of Interviews" suffix="+" data-id="element-25" />
          <div className="flex flex-col items-center text-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100" data-id="element-26">
            <div className="text-4xl md:text-5xl font-serif font-bold text-brand-teal mb-2" data-id="element-27">
              $100K–$100M
            </div>
            <div className="text-sm md:text-base text-gray-600 font-medium uppercase tracking-wide" data-id="element-28">
              Transactions
            </div>
          </div>
          <StatCounter target={6} label="Provinces Represented" data-id="element-29" />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-30">
          {findings.map((item, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: idx * 0.1
        }} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-id="element-31">
              <div className="w-12 h-12 bg-brand-teal/10 rounded-xl flex items-center justify-center mb-6" data-id="element-32">
                {item.icon}
              </div>
              <h3 className="text-xl font-bold text-brand-black mb-3" data-id="element-33">
                {item.title}
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-34">{item.desc}</p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}